import { useField } from "formik";
import { AnimatePresence } from "framer-motion";
import React, { useEffect, useMemo, useState } from "react";
import MaskedInput from "react-text-mask";

import Forms from "../index";

import { addons, theMask } from "./mask";

import { IInputGroupProps } from "./interfaces";

import { formatToCNPJ } from "brazilian-values";
import { Container, Field, Icon, Loader, Message, Tip, Toggle } from "./styles";

const InputGroup: React.FC<IInputGroupProps> = (props) => {
  const { maskGuide, ...rest } = props;

  const [field, meta, helpers] = useField(props.name);

  const [inputType, setInputType] = useState(props.type);

  const handleBlur = (event) => {
    field.onBlur(event);

    if (props.onBlur) props.onBlur(event);
  };

  const masks = useMemo(
    () => ({
      document: ["###.###.###-##", "##.###.###/####-##"],
      phone: ["(++) ####-####", "(++) 9####-####"],
      cpf: "###.###.###-##",
      cnpj: "##.###.###/####-##",
      creditCard: "#### #### #### ####",
      zipcode: "#####-###",
      date: "##/##/####",
      shortDate: "##/####",
      susep: "xxxxxx",
      cvv: "####",
    }),
    []
  );

  const autoCompleteOffWhen = ["zipcode"].includes(props.mask) && "off";

  const handlePasswordVisibility = () => {
    setInputType((state) => (state === "password" ? "text" : "password"));
  };

  const handleMask = (value) => {
    if (!masks[props.mask]) {
      return false;
    }

    return theMask(value, masks[props.mask] || props.mask);
  };

  const handlePaste = (event) => {
    if (props.mask !== "document") return;
    const value = event.clipboardData.getData("text");
    if (value.replace(/\D/g, "").length < 14) return;
    const masked = formatToCNPJ(masks.cnpj);
    helpers.setValue(masked);
  };

  const maskProps = () => {
    return {
      mask:
        {
          money: addons.money,
          percentage: addons.percentage,
        }[props.mask] || handleMask,

      pipe:
        {
          date: addons.date,
          shortDate: addons.shortDate,
        }[props.mask] || false,
    };
  };

  useEffect(() => {
    return () => {
      if (props.resetValueOnUnmount) helpers.setValue("");
    };
  }, []);

  return (
    <Container size={props.size}>
      {props.label && <Forms.Label>{props.label}</Forms.Label>}

      <Field>
        <Forms.Input
          {...props}
          {...field}
          {...maskProps()}
          as={MaskedInput}
          type={inputType}
          guide={maskGuide}
          onBlur={handleBlur}
          onPaste={handlePaste}
          keepCharPositions={true}
          autoComplete={autoCompleteOffWhen}
          error={(meta.touched && !!meta.error) || undefined}
        />

        {props.loading && <Loader />}

        {props.action && (
          <Toggle onClick={props.action.handler}>
            <Icon name={props.action.icon} />
          </Toggle>
        )}

        {props.type === "password" && field.value.length > 0 && (
          <Toggle onClick={handlePasswordVisibility}>
            <Icon name={inputType === "password" ? "eye" : "eye-off"} />
          </Toggle>
        )}
      </Field>

      <AnimatePresence>
        {meta.touched && !!meta.error && (
          <Message kind="danger">{meta.error}</Message>
        )}
      </AnimatePresence>

      {props.tip && (
        <Tip.Wrapper>
          <Tip.Icon />
          {props.tip}
        </Tip.Wrapper>
      )}
    </Container>
  );
};

InputGroup.defaultProps = {
  type: "text",
  maskGuide: false,
  resetValueOnUnmount: true,
};

export default InputGroup;
